import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { AbsenceListItem } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  ABSENCE_TYPE_META, absenceTypeLabel, absenceTypeShort,
  addMonths, formatDateMedium, formatMonthYear, isWeekend, monthMatrix, monthStart, todayYmd,
  weekdayShort,
} from '@/lib/hr';
import { t } from '@/i18n';

interface MonthCalendarProps {
  /** Any day of the displayed month (YYYY-MM-DD). */
  month: string;
  onMonthChange: (monthFirstDay: string) => void;
  /** Map<YYYY-MM-DD, holiday name>. */
  holidays: Map<string, string>;
  /** The employee's own absences (pending + approved are rendered). */
  absences: AbsenceListItem[];
  /** Fired on click (single day) or after a drag across days. */
  onSelectRange: (from: string, to: string) => void;
}

/**
 * The employee's month calendar: weekends + public holidays shaded (they
 * never consume leave), own absences drawn as type-coloured bands, and
 * drag-selection that opens the request sheet with the range prefilled.
 * Keyboard: every in-month day is a button — Enter starts a one-day request.
 */
export function MonthCalendar({ month, onMonthChange, holidays, absences, onSelectRange }: MonthCalendarProps) {
  const today = todayYmd();
  const weeks = useMemo(() => monthMatrix(month), [month]);
  const monthKey = month.slice(0, 7);

  const visible = useMemo(
    () => absences.filter((a) => a.status === 'pending' || a.status === 'approved'),
    [absences],
  );

  const [drag, setDrag] = useState<{ anchor: string; hover: string } | null>(null);
  const selectRef = useRef(onSelectRange);
  selectRef.current = onSelectRange;
  const dragRef = useRef(drag);
  dragRef.current = drag;

  // Finish the drag even when the pointer is released outside the grid.
  useEffect(() => {
    if (!drag) return;
    const finish = () => {
      const current = dragRef.current;
      setDrag(null);
      if (current) {
        const [from, to] = [current.anchor, current.hover].sort();
        selectRef.current(from, to);
      }
    };
    window.addEventListener('mouseup', finish);
    return () => window.removeEventListener('mouseup', finish);
  }, [drag]);

  const inDrag = useCallback(
    (day: string) => {
      if (!drag) return false;
      const [from, to] = [drag.anchor, drag.hover].sort();
      return day >= from && day <= to;
    },
    [drag],
  );

  const absenceOn = useCallback(
    (day: string) => visible.find((a) => a.dateFrom <= day && day <= a.dateTo),
    [visible],
  );

  const legendTypes = useMemo(
    () => [...new Set(visible.map((a) => a.type))],
    [visible],
  );

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base capitalize">{formatMonthYear(monthStart(month))}</CardTitle>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="size-8" aria-label={t('hr.calendar.prevMonth')}
            onClick={() => onMonthChange(addMonths(month, -1))}>
            <ChevronLeft className="size-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-8"
            onClick={() => onMonthChange(monthStart(today))}>
            {t('hr.calendar.today')}
          </Button>
          <Button variant="outline" size="icon" className="size-8" aria-label={t('hr.calendar.nextMonth')}
            onClick={() => onMonthChange(addMonths(month, 1))}>
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground mb-3 text-xs">{t('hr.calendar.hint')}</p>

        <div className="grid select-none grid-cols-7 gap-1">
          {weekdayShort().map((label, i) => (
            <div key={i} className={cn('text-muted-foreground pb-1 text-center text-xs font-medium', i >= 5 && 'opacity-70')}>
              {label}
            </div>
          ))}

          {weeks.flat().map((day) => {
            const inMonth = day.slice(0, 7) === monthKey;
            const holiday = holidays.get(day);
            const off = isWeekend(day) || !!holiday;
            const absence = inMonth ? absenceOn(day) : undefined;
            const meta = absence ? ABSENCE_TYPE_META[absence.type] : null;
            const isToday = day === today;
            const selected = inMonth && inDrag(day);

            if (!inMonth) {
              return <div key={day} aria-hidden className="h-14 rounded-md sm:h-16" />;
            }

            return (
              <button
                key={day}
                type="button"
                aria-label={[
                  formatDateMedium(day),
                  holiday ?? '',
                  absence ? absenceTypeLabel(absence.type) : '',
                ].filter(Boolean).join(', ')}
                className={cn(
                  'relative flex h-14 flex-col rounded-md border p-1 text-left transition-colors sm:h-16',
                  off ? 'bg-muted/60' : 'hover:border-primary/50',
                  selected && 'ring-primary bg-primary/10 ring-2',
                  isToday && !selected && 'border-primary',
                )}
                onMouseDown={(e) => {
                  if (e.button !== 0) return;
                  e.preventDefault();
                  setDrag({ anchor: day, hover: day });
                }}
                onMouseEnter={() => {
                  if (dragRef.current) setDrag({ anchor: dragRef.current.anchor, hover: day });
                }}
                onClick={(e) => {
                  // Mouse selection is handled by the drag lifecycle; e.detail
                  // === 0 means a keyboard "click" (Enter/Space).
                  if (e.detail === 0) onSelectRange(day, day);
                }}
              >
                <span className="flex items-start justify-between gap-1">
                  {holiday ? (
                    <span className="text-muted-foreground min-w-0 truncate text-[10px] leading-tight" title={holiday}>
                      {holiday}
                    </span>
                  ) : <span />}
                  <span
                    className={cn(
                      'text-xs tabular-nums',
                      off && 'text-muted-foreground',
                      isToday &&
                        'bg-primary text-primary-foreground -mr-0.5 -mt-0.5 flex size-5 items-center justify-center rounded-full font-semibold',
                    )}
                  >
                    {parseInt(day.slice(8), 10)}
                  </span>
                </span>
                {absence && meta && (
                  <span
                    className={cn(
                      'mt-auto truncate rounded-sm border px-1 text-[10px] font-medium leading-4',
                      meta.band,
                      absence.status === 'pending' && 'border-dashed opacity-75',
                    )}
                    title={`${absenceTypeLabel(absence.type)} (${t(`hr.status.${absence.status}`)})`}
                  >
                    {day === absence.dateFrom || day === monthStart(day)
                      ? absenceTypeShort(absence.type)
                      : ' '}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="text-muted-foreground mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="bg-muted size-3 rounded-sm border" />
            {t('hr.calendar.legendWeekend')}
          </span>
          {legendTypes.map((type) => (
            <span key={type} className="flex items-center gap-1.5">
              <span className={cn('size-3 rounded-sm border', ABSENCE_TYPE_META[type].band)} />
              {absenceTypeLabel(type)}
            </span>
          ))}
          {visible.some((a) => a.status === 'pending') && (
            <span className="flex items-center gap-1.5">
              <span className="size-3 rounded-sm border border-dashed opacity-75" />
              {t('hr.calendar.legendPending')}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
